import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { emptySchoolObj } from '@app/account/profile/user-profile.variables';
import { DictionaryService } from '@app/core/services/bff/dictionary.service';
import { UserProfileBffService } from '@app/core/services/bff/user-profile.service';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import {
  AuthService,
  ContextService,
  FeaturesService,
  LanguageService,
  MetaService,
  ProfileService,
  SchoolsService,
} from '@core/services';
import { IContentBlock } from '@core/services/interfaces';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { Content, CSchool, ESchoolStatus, IFindSchoolFilter } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, of } from 'rxjs';
import { catchError, takeUntil, tap } from 'rxjs/operators';
import { BaseUserProfileSubpageComponent } from '../base-user-profile-subpage.component';

type SchoolsBlock = IContentBlock<CSchool, IFindSchoolFilter>;

@Component({
  selector: 'app-user-profile-schools-subpage',
  templateUrl: './user-profile-schools-subpage.component.html',
  styleUrls: ['./user-profile-schools-subpage.component.scss'],
})
export class UserProfileSchoolsSubpageComponent extends BaseUserProfileSubpageComponent implements OnInit {
  @Output() public loaded = new EventEmitter<boolean>();

  public schools: SchoolsBlock = {
    items: [],
    loading: false,
    loaded: null,
    filter: {},
    pagination: {
      page: 1,
      pageSize: 10,
      total: 0,
      totalPages: 1,
    },
  };

  public emptySchoolObj = emptySchoolObj;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    protected router: Router,
    protected authService: AuthService,
    protected contextService: ContextService,
    protected profileService: ProfileService,
    protected schoolService: SchoolsService,
    protected userProfileBff: UserProfileBffService,
    protected dictionaryService: DictionaryService,
    protected languageService: LanguageService,
    protected features: FeaturesService,
    protected meta: MetaService,
    protected cdr: ChangeDetectorRef,
    public deviceService: DeviceDetectorService,
    protected loadingBar: LoadingBarService,
  ) {
    super(
      router,
      authService,
      contextService,
      profileService,
      dictionaryService,
      languageService,
      userProfileBff,
      features,
      meta,
      loadingBar,
      cdr,
    );
  }

  public ngOnInit(): void {
    this.loading = true;
    this.loadingBar.fetchingStart(15);

    combineLatest([this.getMe$, this.getUser$.pipe(tap(() => this.loadSchools()))])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.loading = false;
          this.loadingBar.fetchingComplete();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.loading = false;
          this.loadingBar.fetchingStop();
        },
      });
  }

  public get isMyAccount() {
    return this._me && this._user && this._me?.id === this._user.id;
  }

  protected loadSchools(nextPage?: boolean) {
    if (this.schools.loading) {
      return;
    }

    this.schools.loading = true;
    this.loadingBar.fetchingStart(35);
    if (nextPage && this.schools.pagination.page < this.schools.pagination.totalPages) {
      this.schools.pagination.page = this.schools.pagination.page + 1;
    }
    const filter: Partial<IFindSchoolFilter> = {
      ...this.schools.filter,
      participant_account_id: [this._user.id],
    };

    if (!this.isMyAccount) {
      filter.status = [ESchoolStatus.working];
    }

    this.schoolService
      .findSchools(filter, this.schools.pagination)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          if (nextPage) {
            response.results.map((i) => {
              if (!this.schools.items.find((i1) => i1.id === i.id)) {
                this.schools.items.push(i);
              }
            });
          } else {
            this.schools.items = response.results;
          }

          this.schools.pagination.total = response.total;
          this.schools.pagination.totalPages = response.totalPages;
          this.schools.pagination.page = response.page;
          this.schools.loading = false;
          this.schools.loaded = true;
          this.loaded.emit(true);
          this.loadingBar.fetchingComplete();
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
          this.schools.loaded = false;
        },
      });
  }

  public onPublicationsNextPage() {
    this.loadSchools(true);
  }

  public trackContentByFn(index, item: Content): string {
    return item.id;
  }

  public onSchoolFullPrepared(schoolId: string) {
    document.getElementById(schoolId).classList.add('margin');
  }
}
